import React, { useState, useEffect } from 'react';
import {
  Terminal, ChevronDown, ChevronUp, Copy, CheckCircle, XCircle,
  Clock, Search, Filter, Download, Eye, EyeOff, AlertCircle,
  Activity, Shield, Bug, Globe, Server, Database, Wifi, Lock, Key
} from 'lucide-react';
import MarkdownRenderer from './common/MarkdownRenderer';

/**
 * ToolOutputsPanel - Displays detailed outputs from security tools
 * Shows each tool's results with syntax highlighting and filtering
 */
const ToolOutputsPanel = ({ scanId, authToken }) => {
  const [toolData, setToolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTools, setExpandedTools] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (scanId) {
      fetchToolOutputs();
    }
  }, [scanId]);

  const fetchToolOutputs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/scan/${scanId}/tool-outputs`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tool outputs');
      }

      const data = await response.json();
      setToolData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getToolIcon = (toolName, category) => {
    const name = toolName?.toLowerCase() || '';
    const cat = category?.toLowerCase() || '';

    if (name.includes('nmap') || name.includes('masscan')) return Wifi;
    if (name.includes('ssl') || name.includes('tls') || name.includes('testssl')) return Lock;
    if (name.includes('nikto') || name.includes('zap') || name.includes('nuclei')) return Bug;
    if (name.includes('sqlmap') || name.includes('xss')) return Database;
    if (name.includes('hydra') || name.includes('brute')) return Key;
    if (name.includes('gobuster') || name.includes('ffuf') || name.includes('dirsearch')) return Search;
    if (name.includes('harvester') || name.includes('subfinder') || name.includes('dns')) return Globe;
    if (name.includes('metasploit') || name.includes('exploit')) return AlertCircle;
    if (cat === 'reconnaissance' || cat === 'scanner') return Search;
    if (cat === 'exploitation') return AlertCircle;
    if (cat === 'web_attack') return Bug;
    return Terminal;
  };

  const getToolColor = (toolName) => {
    const name = toolName?.toLowerCase() || '';

    if (name.includes('nmap')) return 'text-cyan-400 bg-cyan-500/20';
    if (name.includes('nuclei')) return 'text-purple-400 bg-purple-500/20';
    if (name.includes('nikto')) return 'text-yellow-400 bg-yellow-500/20';
    if (name.includes('ssl') || name.includes('tls')) return 'text-green-400 bg-green-500/20';
    if (name.includes('sqlmap')) return 'text-red-400 bg-red-500/20';
    if (name.includes('hydra')) return 'text-orange-400 bg-orange-500/20';
    if (name.includes('gobuster') || name.includes('ffuf')) return 'text-blue-400 bg-blue-500/20';
    if (name.includes('zap')) return 'text-indigo-400 bg-indigo-500/20';
    if (name.includes('metasploit')) return 'text-red-500 bg-red-500/20';
    return 'text-slate-400 bg-slate-500/20';
  };

  const getStatusBadge = (tool) => {
    if (tool.success) {
      return (
        <span className="flex items-center space-x-1 text-green-400 text-xs">
          <CheckCircle className="w-3 h-3" />
          <span>Success</span>
        </span>
      );
    }
    if (tool.exit_code !== 0 && tool.exit_code !== null) {
      return (
        <span className="flex items-center space-x-1 text-red-400 text-xs">
          <XCircle className="w-3 h-3" />
          <span>Exit: {tool.exit_code}</span>
        </span>
      );
    }
    return (
      <span className="flex items-center space-x-1 text-slate-400 text-xs">
        <Activity className="w-3 h-3" />
        <span>Completed</span>
      </span>
    );
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const highlightOutput = (output, toolName) => {
    if (!output) return null;

    // Remove ANSI color codes
    const cleanOutput = output.replace(/\x1B\[[0-9;]*m/g, '');

    // Highlight patterns based on tool type
    const lines = cleanOutput.split('\n');

    return lines.map((line, idx) => {
      let className = 'text-slate-300';
      let prefix = null;

      // Critical findings
      if (/critical|CRITICAL|CVE-\d{4}-\d+/i.test(line)) {
        className = 'text-red-400 font-semibold';
        prefix = <span className="text-red-500 mr-2">[!]</span>;
      }
      // High severity
      else if (/high|HIGH|vulnerable|VULNERABLE/i.test(line)) {
        className = 'text-orange-400';
        prefix = <span className="text-orange-500 mr-2">[+]</span>;
      }
      // Medium
      else if (/medium|MEDIUM|warning|WARNING/i.test(line)) {
        className = 'text-yellow-400';
      }
      // Open ports
      else if (/\d+\/tcp\s+open/i.test(line)) {
        className = 'text-green-400';
        prefix = <span className="text-green-500 mr-2">[*]</span>;
      }
      // Errors
      else if (/error|ERROR|failed|FAILED/i.test(line)) {
        className = 'text-red-300';
      }
      // Info/success
      else if (/\[\+\]|\[✓\]|success|found/i.test(line)) {
        className = 'text-cyan-400';
      }
      // Headers/sections
      else if (line.startsWith('---') || line.startsWith('===') || line.startsWith('***')) {
        className = 'text-slate-500';
      }

      return (
        <div key={idx} className={`font-mono text-xs ${className}`}>
          {prefix}{line || '\u00A0'}
        </div>
      );
    });
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const toggleTool = (id) => {
    setExpandedTools(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const expandAll = () => {
    const allExpanded = {};
    filteredTools.forEach(tool => {
      allExpanded[tool.id] = true;
    });
    setExpandedTools(allExpanded);
  };

  const collapseAll = () => {
    setExpandedTools({});
  };

  // Get unique categories
  const categories = ['all', ...new Set((toolData?.tools || []).map(t => t.tool_category || 'other').filter(Boolean))];

  // Filter tools
  const filteredTools = (toolData?.tools || []).filter(tool => {
    const matchesSearch = searchTerm === '' ||
      tool.tool_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.stdout?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.command?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = categoryFilter === 'all' ||
      (tool.tool_category || 'other') === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-slate-700 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 rounded-xl p-6 border border-red-500/50">
        <div className="flex items-center space-x-2 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>Failed to load tool outputs: {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header & Filters */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <Terminal className="w-5 h-5 text-cyan-400" />
            <div>
              <h3 className="text-lg font-semibold text-white">Tool Outputs</h3>
              <p className="text-sm text-slate-400">{toolData?.tools_count || 0} tools executed</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search outputs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-400 focus:outline-none focus:border-cyan-500"
              />
            </div>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>

            {/* Expand/Collapse */}
            <div className="flex space-x-2">
              <button
                onClick={expandAll}
                className="px-3 py-2 bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600 rounded-lg text-sm text-slate-300 flex items-center space-x-1 transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span>Expand</span>
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-2 bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600 rounded-lg text-sm text-slate-300 flex items-center space-x-1 transition-colors"
              >
                <EyeOff className="w-4 h-4" />
                <span>Collapse</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tools List */}
      <div className="space-y-3">
        {filteredTools.length === 0 ? (
          <div className="bg-slate-800/50 rounded-xl p-8 border border-slate-700/50 text-center">
            <Terminal className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Aucun resultat d'outil disponible</p>
            <p className="text-slate-500 text-sm mt-2">Les resultats apparaitront ici une fois les outils executes</p>
          </div>
        ) : (
          filteredTools.map((tool) => {
            const IconComponent = getToolIcon(tool.tool_name, tool.tool_category);
            const isExpanded = expandedTools[tool.id];
            const colorClass = getToolColor(tool.tool_name);

            return (
              <div
                key={tool.id}
                className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden"
              >
                {/* Tool Header */}
                <button
                  onClick={() => toggleTool(tool.id)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${colorClass}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-semibold text-white">{tool.tool_name}</span>
                        {tool.tool_category && (
                          <span className="text-xs px-2 py-0.5 bg-slate-700 rounded text-slate-400">
                            {tool.tool_category}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate max-w-lg">
                        {tool.command || tool.target}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {getStatusBadge(tool)}
                    {tool.duration_seconds && (
                      <span className="text-xs text-slate-400 flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{formatDuration(tool.duration_seconds)}</span>
                      </span>
                    )}
                    {tool.findings_count > 0 && (
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                        {tool.findings_count} findings
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-slate-700/50">
                    {/* Command */}
                    {tool.command && (
                      <div className="px-4 py-2 bg-slate-900/50 border-b border-slate-700/50">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">Command:</span>
                          <button
                            onClick={() => copyToClipboard(tool.command, `cmd-${tool.id}`)}
                            className="text-slate-400 hover:text-white transition-colors"
                          >
                            {copiedId === `cmd-${tool.id}` ? (
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <code className="text-xs text-cyan-400 font-mono">{typeof tool.command === 'string' ? tool.command : JSON.stringify(tool.command)}</code>
                      </div>
                    )}

                    {/* Output */}
                    {tool.stdout && (
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-slate-500 uppercase">Output</span>
                          <button
                            onClick={() => copyToClipboard(tool.stdout, `out-${tool.id}`)}
                            className="text-slate-400 hover:text-white transition-colors flex items-center space-x-1 text-xs"
                          >
                            {copiedId === `out-${tool.id}` ? (
                              <>
                                <CheckCircle className="w-3 h-3 text-green-400" />
                                <span className="text-green-400">Copied!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                <span>Copy</span>
                              </>
                            )}
                          </button>
                        </div>
                        <div className="bg-slate-900 rounded-lg p-3 max-h-96 overflow-auto border border-slate-700">
                          {highlightOutput(tool.stdout, tool.tool_name)}
                        </div>
                      </div>
                    )}

                    {/* Stderr if present */}
                    {tool.stderr && (
                      <div className="px-4 pb-4">
                        <span className="text-xs text-red-400 uppercase mb-2 block">Errors</span>
                        <div className="bg-red-900/20 rounded-lg p-3 max-h-40 overflow-auto border border-red-700/50">
                          <pre className="text-xs text-red-300 font-mono whitespace-pre-wrap">{typeof tool.stderr === 'string' ? tool.stderr : JSON.stringify(tool.stderr)}</pre>
                        </div>
                      </div>
                    )}

                    {/* AI Analysis if present */}
                    {tool.ai_analysis && (
                      <div className="px-4 pb-4">
                        <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/30">
                          <div className="flex items-center space-x-2 mb-2">
                            <Shield className="w-4 h-4 text-purple-400" />
                            <span className="text-xs text-purple-400 uppercase">AI Analysis</span>
                          </div>
                          <MarkdownRenderer content={tool.ai_analysis} variant="compact" />
                        </div>
                      </div>
                    )}

                    {/* MITRE ATT&CK Techniques */}
                    {tool.mitre_techniques && tool.mitre_techniques.length > 0 && (
                      <div className="px-4 pb-4">
                        <span className="text-xs text-slate-500 uppercase mb-2 block">MITRE ATT&CK</span>
                        <div className="flex flex-wrap gap-2">
                          {tool.mitre_techniques.map((tech, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded border border-blue-500/30"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* No output message */}
                    {!tool.stdout && !tool.stderr && (
                      <div className="p-4 text-center">
                        <p className="text-sm text-slate-500">No output recorded</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ToolOutputsPanel;
